/* eslint-disable complexity -- response normalization is complex but necessary */
/* eslint-disable @typescript-eslint/no-unsafe-type-assertion -- intentional */
/* eslint-disable @typescript-eslint/consistent-type-assertions -- intentional */

import type { FetchOptions, APIResponse, QueryParams } from '@/types/api.types';
import { API_CONFIG } from '@/config/apiConfig';
import { interceptors } from '@/api/interceptors/interceptor';
import type { CommandAcceptedJson, CommandAcceptedBody200 } from '@/types/polling.types';
import { MIN_LEN } from '@/constants';
import { getSessionToken, clearAuthTokens } from '@/utils/sessionStorage';
import { refreshSessionTokens } from '@/utils/authSession';

const DEFAULT_OPTIONS: FetchOptions = {
  baseURL: API_CONFIG.baseURL,
  headers: API_CONFIG.defaultHeaders,
  credentials: 'include',
};

const HTTP_STATUS_ACCEPTED = 202;
const HTTP_STATUS_UNAUTHORIZED = 401;
const EMPTY_BODY_THRESHOLD = 0;
const COMMANDS_PREFIX = 'v1/commands/';

function isAuthEndpoint(endpoint: string): boolean {
  const normalizedEndpoint = endpoint.trim().toLowerCase();
  return (
    normalizedEndpoint.startsWith('/public/v1/auth/') ||
    normalizedEndpoint.startsWith('/private/v1/auth/') ||
    normalizedEndpoint.includes('/login') ||
    normalizedEndpoint.includes('/registration') ||
    normalizedEndpoint.includes('/refresh')
  );
}

function setAuthorizationHeader(options: FetchOptions, token: string | null): FetchOptions {
  const headers = { ...(options.headers ?? {}) };
  if (token !== null && token.trim().length > MIN_LEN) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    delete (headers as Record<string, unknown>).Authorization;
  }
  return { ...options, headers };
}

/* -------------------- Helpers -------------------- */

function buildUrl(baseURL: string, endpoint: string, params?: QueryParams): string {
  const url = new URL(`${baseURL}${endpoint}`);
  if (params !== undefined) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) url.searchParams.append(key, String(value));
    }
  }
  return url.toString();
}

function toAbsoluteUrl(baseURL: string, maybeRelativeOrAbsolute: string): string {
  if (maybeRelativeOrAbsolute.startsWith('http')) return maybeRelativeOrAbsolute;
  return new URL(maybeRelativeOrAbsolute.replace(/^\/+/, ''), baseURL.replace(/\/+$/, '/') + '/').toString();
}

async function safeParseJson(response: Response): Promise<object | null> {
  try {
   const clonedResponse: Response = response.clone();
    const jsonResult: unknown = await clonedResponse.json();

    if (typeof jsonResult === 'object' && jsonResult !== null) {
      return jsonResult as Record<string, unknown>;
    }

    return null;
  } catch {
    return null;
  }
}

function looksLikeCommandAcceptedBody(x: unknown): x is CommandAcceptedBody200 {
  return typeof x === 'object' && x !== null && typeof (x as Record<string, unknown>).commandId === 'string';
}

/* -------------------- Normalizers -------------------- */

/**
 * Normalize HTTP 202 responses:
 * - Prefer Location/Operation-Location headers
 * - Otherwise look for wrapped payload.commandId in body
 * - Fallback to legacy { statusUrl, resourceUrl } in body
 */
async function normalizeAcceptedResponse(response: Response): Promise<CommandAcceptedJson | null> {
  const headerStatusUrl = response.headers.get('Location') ?? response.headers.get('Operation-Location') ?? undefined;
  const headerResourceLocation = response.headers.get('Resource-Location') ?? undefined;

  if (headerStatusUrl !== undefined) {
    return { statusUrl: headerStatusUrl, resourceUrl: headerResourceLocation };
  }

  const parsedBody = await safeParseJson(response);
  if (typeof parsedBody === 'object' && parsedBody !== null) {
    const wrapper = parsedBody as Record<string, unknown>;

    // New wrapper: { httpCode, success, message, payload: { commandId, resourceLink, ... } }
    if ('payload' in wrapper && typeof wrapper.payload === 'object' && wrapper.payload !== null) {
      const payloadObj = wrapper.payload as Record<string, unknown>;
      if (typeof payloadObj.commandId === 'string') {
        const commandId = String(payloadObj.commandId);
        const statusUrl = toAbsoluteUrl(API_CONFIG.baseURL, `${COMMANDS_PREFIX}${commandId}`);
        const resourceUrl = typeof payloadObj.resourceLink === 'string'
          ? toAbsoluteUrl(API_CONFIG.baseURL, payloadObj.resourceLink)
          : undefined;

        const { commandId: _omit1, resourceLink: _omit2, ...meta } = payloadObj;
        return { statusUrl, resourceUrl, meta };
      }
    }

    // Legacy body: { statusUrl, resourceUrl }
    if ('statusUrl' in wrapper && typeof wrapper.statusUrl === 'string') {
      return {
        statusUrl: String(wrapper.statusUrl),
        resourceUrl: typeof wrapper.resourceUrl === 'string' ? String(wrapper.resourceUrl) : undefined,
      };
    }
  }

  return null;
}

/**
 * Normalize 200 (CQRS) responses that include commandId either at top-level
 * or inside wrapper.payload.
 */
async function normalizeOkCQRSResponse(
  response: Response,
  baseURL: string
): Promise<CommandAcceptedJson<Record<string, unknown>> | null> {
  const parsedBody = await safeParseJson(response);
  if (parsedBody === null || typeof parsedBody !== 'object') return null;

  const bodyObj = parsedBody as Record<string, unknown>;

  // Top-level command body: { commandId, resourceLink, ... }
  if (looksLikeCommandAcceptedBody(bodyObj)) {
    const commandId = String(bodyObj.commandId);
    const statusUrl = toAbsoluteUrl(baseURL, `${COMMANDS_PREFIX}${commandId}`);
    const resourceUrl = typeof bodyObj.resourceLink === 'string' ? toAbsoluteUrl(baseURL, bodyObj.resourceLink) : undefined;
    const { commandId: _omit1, resourceLink: _omit2, ...meta } = bodyObj;
    return { statusUrl, resourceUrl, meta };
  }

  // Wrapped command body: { payload: { commandId, resourceLink, ... } }
  if ('payload' in bodyObj && typeof bodyObj.payload === 'object' && bodyObj.payload !== null) {
    const payloadObj = bodyObj.payload as Record<string, unknown>;
    if (typeof payloadObj.commandId === 'string') {
      const commandId = String(payloadObj.commandId);
      const statusUrl = toAbsoluteUrl(baseURL, `${COMMANDS_PREFIX}${commandId}`);
      const resourceUrl = typeof payloadObj.resourceLink === 'string' ? toAbsoluteUrl(baseURL, payloadObj.resourceLink) : undefined;
      const { commandId: _omit1, resourceLink: _omit2, ...meta } = payloadObj;
      return { statusUrl, resourceUrl, meta };
    }
  }

  return null;
}

/* -------------------- Main API client -------------------- */

export async function apiClient<T>(
  endpoint: string,
  options: FetchOptions = {},
  params?: QueryParams
): Promise<APIResponse<T> | CommandAcceptedJson> {
  // Merge default options and provided options
  let mergedOptions: FetchOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
    headers: { ...DEFAULT_OPTIONS.headers, ...options.headers },
  };

  // Apply request interceptors
  for (const requestInterceptor of interceptors.request) {
    mergedOptions = await requestInterceptor(mergedOptions);
  }

  const { baseURL, ...fetchOptions } = mergedOptions;
  if (baseURL === undefined) throw new Error('Missing baseURL in API client options');

  const fullUrl = buildUrl(baseURL, endpoint, params);

  // Perform the request and run response interceptors
  let response = await fetch(fullUrl, fetchOptions);

    const canAttemptRefresh =
    response.status === HTTP_STATUS_UNAUTHORIZED &&
    !isAuthEndpoint(endpoint);

    if (canAttemptRefresh) {
    const refreshed = await refreshSessionTokens(baseURL);

    if (refreshed) {
      const sessionToken = getSessionToken();
      const retryOptions = setAuthorizationHeader({ ...mergedOptions }, sessionToken);
      const { baseURL: _retryBaseUrl, ...retryFetchOptions } = retryOptions;

      response = await fetch(fullUrl, retryFetchOptions);
    } else {
      clearAuthTokens();
      window.location.replace('/login');
    }
  }

  for (const responseInterceptor of interceptors.response) {
    response = await responseInterceptor(response);
  }

  /* ---------- 1) HTTP 202 (Accepted) CQRS handling ---------- */
  if (response.status === HTTP_STATUS_ACCEPTED) {
    const acceptedResult = await normalizeAcceptedResponse(response);
    if (acceptedResult !== null) return acceptedResult;
  }

  /* ---------- 2) 200 OK but CQRS body (commandId) ---------- */
  if (response.ok) {
    const accepted200 = await normalizeOkCQRSResponse(response, baseURL);
    if (accepted200 !== null) return accepted200;
  }

// ---- Error branch: replace existing `if (!response.ok) { ... }` with this ----
if (!response.ok) {
  let extractedMessage: string | undefined = undefined;

  // Try to parse JSON safely (helper returns `unknown | null`)
  const parsed = await safeParseJson(response);

  if ((parsed !== null) && typeof parsed === 'object') {
    const wrapper = parsed as Record<string, unknown>;

    // 1) Prefer a top-level "message" string if present and non-empty
    const { message } = wrapper
    const topLevel = message;
    if (typeof topLevel === 'string' && topLevel.trim().length > MIN_LEN) {
      extractedMessage = topLevel.trim();
    } else {
      // 2) Otherwise, if there is a `payload` object, try payload.message
       const { payload } = wrapper
      const maybePayload = payload;
      if ((Boolean(maybePayload)) && typeof maybePayload === 'object') {
        const payloadObj = maybePayload as Record<string, unknown>;
         const { message } = payloadObj
        const payloadMsg = message;
        if (typeof payloadMsg === 'string' && payloadMsg.trim().length > MIN_LEN) {
          extractedMessage = payloadMsg.trim();
        }
      }
    }
  }

  // 3) Fallback to response.statusText if available, else generic message
  const fallbackMessage =
    typeof response.statusText === 'string' && response.statusText.trim().length > MIN_LEN
      ? response.statusText.trim()
      : 'Request failed';

  throw new Error(extractedMessage ?? fallbackMessage);
}
  /* ---------- 4) OK responses: unwrap wrapper.payload or return the parsed DTO ---------- */
  const responseText = await response.text();
  if (responseText.length <= EMPTY_BODY_THRESHOLD) return {} as APIResponse<T>;

  const parsedRaw: unknown = JSON.parse(responseText);

  if (typeof parsedRaw === 'object' && parsedRaw !== null) {
    const parsedObj = parsedRaw as Record<string, unknown>;

    // New wrapper shape: { httpCode, success, message, payload }
    if ('payload' in parsedObj) {
      return parsedObj.payload as APIResponse<T>;
    }

    // Old style: return the parsed object as-is
    return parsedObj as APIResponse<T>;
  }

  // Primitive JSON fallback
  return parsedRaw as APIResponse<T>;
}
